import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, shallowRef } from "vue";
import { useStore } from "@/stores";
import { showConfirm, showSuccess, showWarning } from "@/utils/UI";
import { API_BIS_CATEGORY, API_BIS_CATEGORY_SYNC, API_BIS_FILTER } from "@/configs/Apis";
import { isEmptyOrWhiteSpace, isValidId } from "@/utils/Common";
import DuduAjax from "@/utils/DuduAjax";
import cloneDeep from "lodash/cloneDeep";

import AdvSearch from "@/views/components/AdvSearch.vue";
import { CondOperator } from "@/app-data/enum";
import { CondObject } from "@/types";


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomFilterPanel',
  emits: ["search"],
  setup(__props, { expose, emit }) {


const store = useStore();

const categoryFormRef = shallowRef();
const categoryAdvtRef = shallowRef();
const customFilterFormRef = shallowRef();
const queryCategoryFormRef = shallowRef();
const advtRef = shallowRef();
const queryAdvtRef = shallowRef();
const categoryTreeRef = shallowRef();
const filterTreeRef = shallowRef();

const projectId = computed(() => (store.state as any).project.info.id);

const activeNames = ref(["1", "2"]);
const currentCategory = ref({ id: "", name: "", parentId: 0, condition: "" });
const currentCustomFilter = ref({ name: "", filterCondition: "", markPublic: false, id: "" });
const queryCategory = ref({
  id: "",
  name: "",
  parentId: 0,
  queryString: "",
  queryType: "LOCAL",
  condition: "",
});

const showCustomFilterWin = ref(false);
const showCategoryWin = ref(false);
const showQueryCategoryWin = ref(false);

const expandNodesMap = new Set();
const customCategoryRules = { name: [{ required: true, message: "请输入分类名称", trigger: "blur" }] };
const categoryTreeProps = { label: "name", children: "children" };
const filterTreeProps = { label: "title", children: "children" };

const categoryLoading = ref(false);

const categoryTreeData = computed(() => {
  const items = (store.state as any).project.customCategoryTree;
  if (items && items.length) {
    return [{ id: 0, name: "全部" }].concat(items);
  }
  return items;
});

const filterData = computed(() => {
  const items = (store.state as any).project.customFilterData;
  if (items && items.length > 0) {
    return [{ nodeId: 0, title: "全部", patentFilter: "" }].concat(items);
  }
  return items;
});

const appendFields = computed(() => {
  const fields = [];
  const categoryData = categoryTreeData.value;
  if (!isEmptyOrWhiteSpace(categoryData)) {
    const categoryQueryFields = [];
    categoryQueryFields.push(
      ...categoryData.map((field: any) => ({
        name: `category${field.id}`,
        label: field.name,
        type: "tree",
        treeData: cloneDeep(field.children || []),
      }))
    );
    fields.push({ name: "自定义分类", fields: categoryQueryFields });
  }
  const customFields = store.getters["project/customfields"];
  if (customFields && customFields.length) {
    const customQueryFields = [];
    customQueryFields.push(
      ...customFields.map((field: any) => {
        const item: any = { name: field.fieldName.replace("_", ""), label: field.label };
        if (field.fieldType === "ENUM") {
          item.type = "enum";
          item.enumValues = [];
          if (!isEmptyOrWhiteSpace(field.enumValues)) {
            item.enumValues.push(...field.enumValues.split(/[\r\n;]/g));
          }
        } else {
          item.type = "text";
        }
        return item;
      })
    );
    fields.push({ name: "标引字段", fields: customQueryFields });
  }
  return fields;
});

const onCategoryNodeExpand = (data: any) => {
  expandNodesMap.add(data.id);
};

const onCategoryNodeCollapse = (data: any) => {
  expandNodesMap.delete(data.id);
};

const onCategoryNodeClick = (data: any) => {
  filterTreeRef.value.setCurrentKey(null);
  // 「全部」虚拟节点: 不做任何过滤, 显示全部
  if (data.id === 0) {
    emit("search", JSON.stringify({ items: [], relation: "1" }));
    return;
  }
  if (!isEmptyOrWhiteSpace(data.queryCondition)) {
    emit("search", data.queryCondition);
    return;
  }
  // 统一走 category 关联表过滤: NOT_NULL + category{id}。
  const condObj: CondObject = { items: [], relation: "1" };
  condObj.items.push({
    name: `category${data.id}`,
    value1: data.id,
    value2: "",
    operator: CondOperator.NOT_NULL,
  });
  emit("search", JSON.stringify(condObj));
};

const onFilterNodeClick = (node: any) => {
  categoryTreeRef.value.setCurrentKey(null);
  emit("search", node.patentFilter?.filterCondition ?? "");
};

const loadCategoryTree = async () => {
  await store.dispatch("project/getCustomCategoryData");
};

const addCategory = (data: any) => {
  showCategoryWin.value = true;
  currentCategory.value = { id: "", name: "", parentId: data?.id ?? 0, condition: "" };
};

const addQueryCategory = (data: any) => {
  showQueryCategoryWin.value = true;
  queryCategory.value = {
    id: "",
    name: "",
    parentId: data?.id ?? 0,
    queryString: "",
    queryType: "LOCAL",
    condition: "",
  };
};

const editCategory = (data: any) => {
  if (!isEmptyOrWhiteSpace(data.queryString)) {
    // 检索式分类: 打开检索式弹窗, 避免编辑丢失检索式
    showQueryCategoryWin.value = true;
    queryCategory.value = {
      id: data.id,
      name: data.name,
      parentId: data.parentId || 0,
      queryString: data.queryString,
      queryType: data.queryType || "LOCAL",
      condition: hasQueryItems(data.queryString) ? data.queryString : data.queryCondition || "",
    };
    return;
  }
  showCategoryWin.value = true;
  currentCategory.value = { ...JSON.parse(JSON.stringify(data)), condition: "" };
};

const deleteCategory = (data: any) => {
  showConfirm("是否删除这个分类").then(async () => {
    await DuduAjax.delete({ url: API_BIS_CATEGORY, path: { projectId: projectId.value, id: data.id } }, data.id);
    await loadCategoryTree();
    showSuccess("删除成功");
  });
};

const closeQueryCategoryWin = () => {
  showQueryCategoryWin.value = false;
  queryCategory.value = {
    id: "",
    name: "",
    parentId: 0,
    queryString: "",
    queryType: "LOCAL",
    condition: "",
  };
};

const buildWipsQuery = (advRef: any = queryAdvtRef) => {
  const adv = advRef?.value as any;
  return adv ? adv.getSearchData() : "";
};

/** 判断条件 JSON 中是否包含有效条件项 */
const hasQueryItems = (queryJson: string) => {
  if (isEmptyOrWhiteSpace(queryJson)) {
    return false;
  }
  try {
    const cond: CondObject = JSON.parse(queryJson);
    return !!(cond.items && cond.items.length);
  } catch (e) {
    return false;
  }
};

const saveQueryCategory = () => {
  (queryCategoryFormRef.value as any).validate(async (isValid: boolean) => {
    if (!isValid) {
      return;
    }
    // 允许清空条件保存(转为普通分类), 后端收敛会清理旧 AUTO 关联
    const conditionJson = buildWipsQuery();
    const hasCondition = !isEmptyOrWhiteSpace(conditionJson) && hasQueryItems(conditionJson);
    const url: any = { url: API_BIS_CATEGORY, path: { projectId: projectId.value } };
    if (isValidId(queryCategory.value.id)) {
      url.path.id = queryCategory.value.id;
    }
    const newData = {
      id: queryCategory.value.id || 0,
      name: queryCategory.value.name,
      parentId: queryCategory.value.parentId || 0,
      queryString: hasCondition ? conditionJson : null,
      queryType: hasCondition ? "LOCAL" : null,
    };

    try {
      categoryLoading.value = true;
      await (isValidId(queryCategory.value.id) ? DuduAjax.put(url, newData) : DuduAjax.post(url, newData));
      showSuccess("保存成功");
      if (queryCategory.value.parentId) {
        expandNodesMap.add(Number(queryCategory.value.parentId));
      }
      await loadCategoryTree();
      closeQueryCategoryWin();
    } finally {
      categoryLoading.value = false;
    }
  });
};

const syncQueryCategory = (data: any) => {
  showConfirm("是否以该分类的检索式发起数据同步？").then(async () => {
    const res: any = await DuduAjax.post(
      { url: API_BIS_CATEGORY_SYNC, path: { projectId: projectId.value, id: data.id } },
      {}
    );
    store.dispatch("synctask/progressDataSync", {
      projectId: projectId.value,
      taskId: res.requestUuid,
    });
    showSuccess("同步任务已提交，请在任务列表中查看进度");
  });
};

const saveCategory = () => {
  (categoryFormRef.value as any).validate(async (isValid: boolean) => {
    if (!isValid) {
      return;
    }
    const url: any = { url: API_BIS_CATEGORY, path: { projectId: projectId.value } };
    if (isValidId(currentCategory.value.id)) {
      url.path.id = currentCategory.value.id;
    }
    const newData = {};
    const category = currentCategory.value;

    try {
      categoryLoading.value = true;
      // 下方条件 = 下拉选择的本地条件 JSON, 直接存入, 不转 WIPS 检索式; 空则视为普通手动分类
      const queryString = buildWipsQuery(categoryAdvtRef);
      const hasCondition = !isEmptyOrWhiteSpace(queryString) && hasQueryItems(queryString);
      Object.assign(newData, {
        id: category.id || 0,
        name: category.name,
        parentId: category.parentId || 0,
        queryString: hasCondition ? queryString : null,
        queryType: hasCondition ? "LOCAL" : null,
      });
      await (isValidId(currentCategory.value.id) ? DuduAjax.put(url, newData) : DuduAjax.post(url, newData));
      showSuccess("保存成功");
      if (category.parentId) {
        expandNodesMap.add(Number(category.parentId));
      }
      await loadCategoryTree();
      closeCategoryWin();
    } finally {
      categoryLoading.value = false;
    }
  });
};

const closeCategoryWin = () => {
  showCategoryWin.value = false;
  currentCategory.value = { id: "", name: "", parentId: 0, condition: "" };
};

const loadCustomFilter = async () => {
  await store.dispatch("project/getCustomFilterData");
};

const addFilter = () => {
  showCustomFilterWin.value = true;
  currentCustomFilter.value = { name: "", filterCondition: "", markPublic: false, id: "" };
};

const deleteFilter = async (node: any) => {
  const data = node.data;
  await showConfirm("是否删除这个筛选器");
  await DuduAjax.delete(
    {
      url: API_BIS_FILTER,
      path: { projectId: projectId.value, id: data.nodeId },
    },
    data.nodeId
  );
  await loadCustomFilter();
  showSuccess("删除成功");
};

const editFilter = (node: any, data: any) => {
  showCustomFilterWin.value = true;
  currentCustomFilter.value = cloneDeep(data.patentFilter);
};

const saveCustomFilter = () => {
  (customFilterFormRef.value as any).validate(async (isValid: boolean) => {
    if (!isValid) {
      return;
    }
    const adv = advtRef.value as any;
    const filter = currentCustomFilter.value;
    const newData: any = {};

    Object.assign(newData, {
      id: filter.id || 0,
      name: filter.name,
      filterCondition: adv.getSearchData(),
      markPublic: filter.markPublic,
    });

    if (isValidId(filter.id)) {
      await DuduAjax.put(
        {
          url: API_BIS_FILTER,
          path: { projectId: projectId.value, id: filter.id || "" },
        },
        newData
      );
    } else {
      await DuduAjax.post(
        {
          url: API_BIS_FILTER,
          path: { projectId: projectId.value, id: filter.id || "" },
        },
        newData
      );
    }
    closeCustomFilterWin();
    await loadCustomFilter();
    showSuccess("保存成功");
  });
};

const closeCustomFilterWin = () => {
  showCustomFilterWin.value = false;
  currentCustomFilter.value = { id: "", name: "", filterCondition: "", markPublic: false };
};

const clearSelectedItems = () => {
  categoryTreeRef.value.setCurrentKey(null);
  filterTreeRef.value.setCurrentKey(null);
};

expose({ clearSelectedItems });

return { __sfc: true,emit, store, categoryFormRef, categoryAdvtRef, customFilterFormRef, queryCategoryFormRef, advtRef, queryAdvtRef, categoryTreeRef, filterTreeRef, projectId, activeNames, currentCategory, currentCustomFilter, queryCategory, showCustomFilterWin, showCategoryWin, showQueryCategoryWin, expandNodesMap, customCategoryRules, categoryTreeProps, filterTreeProps, categoryLoading, categoryTreeData, filterData, appendFields, onCategoryNodeExpand, onCategoryNodeCollapse, onCategoryNodeClick, onFilterNodeClick, loadCategoryTree, addCategory, addQueryCategory, editCategory, deleteCategory, closeQueryCategoryWin, buildWipsQuery, hasQueryItems, saveQueryCategory, syncQueryCategory, saveCategory, closeCategoryWin, loadCustomFilter, addFilter, deleteFilter, editFilter, saveCustomFilter, closeCustomFilterWin, clearSelectedItems, AdvSearch }
}

})